/**
 * USB HID 分析器核心模块
 */

import type {
  DeviceInfo,
  DeviceType,
  USBTimingParameters,
  USBSpeedClass,
  TimingStability,
  TimingPattern,
  FramePattern,
  MicroframePattern,
  ReliabilityLadder,
  BurstAnalysis,
  BurstInfo,
  ConfidenceResult,
  ConfidenceIssue,
  AnalysisResult,
  AnalyzerEvents,
  EventCallback,
} from '../types';
import { CONFIG, USB_CONSTANTS, DEVICE_TYPE_MAP, GRADE_THRESHOLDS } from '../config';
import * as Stats from './statistics';

type EventKey = keyof AnalyzerEvents;

export interface MeasurementRecord {
  timestamp: number;
  rate: number;
  samples: number;
}

export class USBHIDAnalyzer {
  private device: HIDDevice | null = null;
  private timestamps: number[] = [];
  private intervals: number[] = [];
  private isCapturing = false;
  private reportId = 0;
  private deviceInfo: DeviceInfo | null = null;
  private deviceSpeed: USBTimingParameters | null = null;
  private effectiveRate = 0;

  // 测量历史记录
  private measurementHistory: MeasurementRecord[] = [];
  private static readonly MAX_HISTORY = 5;

  // 事件监听器
  private listeners: Map<EventKey, Set<EventCallback<unknown>>> = new Map();

  // ==================== 事件系统 ====================

  on<K extends EventKey>(event: K, callback: EventCallback<AnalyzerEvents[K]>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback<unknown>);
  }

  off<K extends EventKey>(event: K, callback: EventCallback<AnalyzerEvents[K]>): void {
    this.listeners.get(event)?.delete(callback as EventCallback<unknown>);
  }

  private emit<K extends EventKey>(event: K, data: AnalyzerEvents[K]): void {
    this.listeners.get(event)?.forEach(cb => cb(data));
  }

  // ==================== 设备连接 ====================

  async connect(): Promise<boolean> {
    try {
      // 只允许手柄设备 (usagePage: 1, usage: 4 或 5)
      const devices = await navigator.hid.requestDevice({
        filters: [
          { usagePage: 0x01, usage: 0x04 }, // 游戏手柄
          { usagePage: 0x01, usage: 0x05 }, // 摇杆
        ],
      });
      if (!devices.length) {
        return false;
      }

      this.device = devices[0];

      if (!this.device.opened) {
        await this.device.open();
      }

      this.deviceInfo = this.extractDeviceInfo();
      this.emit('device:connected', this.deviceInfo);

      return true;
    } catch (error) {
      this.emit('error', error as Error);
      return false;
    }
  }

  disconnect(): void {
    if (this.device?.opened) {
      this.device.close();
    }

    this.device = null;
    this.deviceInfo = null;
    this.timestamps = [];
    this.intervals = [];
    this.deviceSpeed = null;
    this.isCapturing = false;
    this.reportId = 0;
    this.effectiveRate = 0;
    this.measurementHistory = [];

    this.emit('device:disconnected', undefined);
  }

  getDeviceInfo(): DeviceInfo | null {
    return this.deviceInfo;
  }

  isConnected(): boolean {
    return this.device !== null && this.device.opened;
  }

  // ==================== 设备信息提取 ====================

  private extractDeviceInfo(): DeviceInfo {
    if (!this.device) {
      throw new Error('No device connected');
    }

    const { type, hint } = this.detectDeviceType();
    this.reportId = this.getReportId();

    return {
      name: this.device.productName || '未知设备',
      vendorId: this.device.vendorId,
      productId: this.device.productId,
      type,
      hint,
      reportId: this.reportId,
    };
  }

  private detectDeviceType(): { type: DeviceType; hint: string } {
    if (!this.device) {
      return { type: 'hid', hint: '' };
    }

    for (const col of this.device.collections) {
      if (col.usagePage === 1 && col.usage !== undefined) {
        const key = (col.usagePage << 8) | col.usage;
        const mapping = DEVICE_TYPE_MAP[key];
        if (mapping) {
          return {
            type: 'gamepad',
            hint: mapping.hint,
          };
        }
      }
    }

    return { type: 'hid', hint: '' };
  }

  private getReportId(): number {
    if (!this.device) return 0;

    for (const col of this.device.collections) {
      if (col.inputReports?.length) {
        return col.inputReports[0].reportId ?? 0;
      }
    }
    return 0;
  }

  // ==================== 数据采集 ====================

  async startCapture(duration = CONFIG.capture.duration): Promise<void> {
    if (!this.device || this.isCapturing) return;

    this.isCapturing = true;
    this.timestamps = [];
    this.intervals = [];

    this.emit('capture:start', undefined);

    const startTime = performance.now();
    let sampleCount = 0;

    const handler = (event: HIDInputReportEvent) => {
      if (event.reportId === this.reportId) {
        const now = performance.now();
        this.timestamps.push(now);
        sampleCount++;

        // 防止内存溢出
        if (sampleCount >= CONFIG.capture.maxSamples) {
          this.device?.removeEventListener('inputreport', handler);
        }

        // 实时计算轮询率预览（每100个样本更新一次）
        let realtimeRate = 0;
        if (this.timestamps.length >= 10) {
          const recentCount = Math.min(100, this.timestamps.length);
          const recentStart = this.timestamps[this.timestamps.length - recentCount];
          const recentEnd = this.timestamps[this.timestamps.length - 1];
          const recentDuration = recentEnd - recentStart;
          if (recentDuration > 0) {
            realtimeRate = ((recentCount - 1) / recentDuration) * 1000;
          }
        }

        this.emit('capture:progress', {
          count: sampleCount,
          elapsed: now - startTime,
          realtimeRate,
        });
      }
    };

    this.device.addEventListener('inputreport', handler);

    await new Promise(resolve => setTimeout(resolve, duration));

    this.device.removeEventListener('inputreport', handler);

    // 预热期过滤：丢弃初始不稳定样本
    const warmupTime = CONFIG.capture.warmupTime;
    if (warmupTime > 0 && this.timestamps.length > 0) {
      const captureStartTime = this.timestamps[0];
      const warmupEndTime = captureStartTime + warmupTime;

      // 找到第一个超过预热期的时间戳索引
      let warmupEndIndex = 0;
      for (let i = 0; i < this.timestamps.length; i++) {
        if (this.timestamps[i] >= warmupEndTime) {
          warmupEndIndex = i;
          break;
        }
      }

      // 丢弃预热期的样本
      if (warmupEndIndex > 0) {
        this.timestamps = this.timestamps.slice(warmupEndIndex);
      }
    }

    // 计算间隔（微秒）
    for (let i = 1; i < this.timestamps.length; i++) {
      this.intervals.push((this.timestamps[i] - this.timestamps[i - 1]) * 1000);
    }

    this.isCapturing = false;

    this.emit('capture:complete', {
      timestamps: this.timestamps,
      intervals: this.intervals,
    });
  }

  // ==================== USB 时序参数 ====================

  private getUSBTimingParameters(measuredRate: number): USBTimingParameters {
    let speedClass: USBSpeedClass;
    let maxPossibleRate: number;
    let minInterval: number;
    let typicalRates: number[];

    if (measuredRate <= USB_CONSTANTS.LOW_SPEED_MAX_RATE * 1.1) {
      speedClass = 'LowSpeed';
      maxPossibleRate = USB_CONSTANTS.LOW_SPEED_MAX_RATE;
      minInterval = 8000; // 8ms
      typicalRates = [125];
    } else if (measuredRate <= USB_CONSTANTS.FULL_SPEED_MAX_RATE * 1.1) {
      speedClass = 'FullSpeed';
      maxPossibleRate = USB_CONSTANTS.FULL_SPEED_MAX_RATE;
      minInterval = USB_CONSTANTS.FRAME_DURATION_US;
      typicalRates = [125, 250, 500, 1000];
    } else {
      speedClass = 'HighSpeed';
      maxPossibleRate = USB_CONSTANTS.HIGH_SPEED_MAX_RATE;
      minInterval = USB_CONSTANTS.MICROFRAME_DURATION_US;
      typicalRates = [1000, 2000, 4000, 8000];
    }

    return {
      speedClass,
      maxPossibleRate,
      frameSize: USB_CONSTANTS.FRAME_DURATION_US,
      microframesPerFrame: speedClass === 'HighSpeed' ? 8 : 1,
      minInterval,
      typicalRates,
    };
  }

  // ==================== 分析方法 ====================

  analyze(): AnalysisResult | null {
    if (this.intervals.length < CONFIG.capture.minSamples) {
      this.emit('error', new Error(
        `样本数不足: ${this.intervals.length}/${CONFIG.capture.minSamples}`
      ));
      return null;
    }

    // 使用"样本数/时长"方法计算轮询率（最准确，不受批处理和量化影响）
    // 总时长 = 最后一个时间戳 - 第一个时间戳（毫秒）
    const totalDurationMs = this.timestamps[this.timestamps.length - 1] - this.timestamps[0];

    if (totalDurationMs > 0) {
      // 轮询率 = (样本数 - 1) / 时长(秒)
      // 样本数 - 1 是因为 n 个时间戳只有 n-1 个间隔
      this.effectiveRate = ((this.timestamps.length - 1) / totalDurationMs) * 1000;
    } else {
      // 备用方案：使用 IQR 截断平均
      this.effectiveRate = Stats.estimateTruePollingRate(this.intervals);
    }

    // 防止无效的轮询率
    if (!this.effectiveRate || !isFinite(this.effectiveRate) || this.effectiveRate <= 0) {
      this.effectiveRate = 1000; // 默认 1000Hz
    }

    this.deviceSpeed = this.getUSBTimingParameters(this.effectiveRate);

    // 计算各项指标
    const timing = this.computeTimingStability();
    const pattern = this.computeTimingPattern();
    const reliability = this.computeReliabilityLadder();
    const bursts = this.detectBurstPatterns();
    const confidence = this.computeMeasurementConfidence();
    const histogram = Stats.generateHistogram(this.intervals);

    // 计算效率
    const efficiency = Math.min(
      100,
      (this.effectiveRate / this.deviceSpeed.maxPossibleRate) * 100
    );

    const result: AnalysisResult = {
      device: this.deviceInfo!,
      deviceSpeed: this.deviceSpeed,
      captureInfo: {
        duration: CONFIG.capture.duration,
        sampleCount: this.timestamps.length,
        startTime: this.timestamps[0],
        endTime: this.timestamps[this.timestamps.length - 1],
      },
      metrics: {
        effectiveRate: this.effectiveRate,
        efficiency,
        timing,
        pattern,
        reliability,
        bursts,
        confidence,
      },
      rawData: {
        timestamps: this.timestamps,
        intervals: this.intervals,
      },
      histogram,
    };

    // 添加到测量历史
    this.measurementHistory.push({
      timestamp: Date.now(),
      rate: this.effectiveRate,
      samples: this.timestamps.length,
    });

    // 保留最近的记录
    if (this.measurementHistory.length > USBHIDAnalyzer.MAX_HISTORY) {
      this.measurementHistory.shift();
    }

    this.emit('analysis:complete', result);
    return result;
  }

  /**
   * 获取测量历史记录
   */
  getMeasurementHistory(): MeasurementRecord[] {
    return [...this.measurementHistory];
  }

  /**
   * 获取最佳测量结果
   */
  getBestMeasurement(): MeasurementRecord | null {
    if (this.measurementHistory.length === 0) return null;
    return this.measurementHistory.reduce((best, current) =>
      current.rate > best.rate ? current : best
    );
  }

  /**
   * 获取平均测量结果
   */
  getAverageMeasurement(): number {
    if (this.measurementHistory.length === 0) return 0;
    const sum = this.measurementHistory.reduce((acc, m) => acc + m.rate, 0);
    return sum / this.measurementHistory.length;
  }

  /**
   * 清空测量历史
   */
  clearHistory(): void {
    this.measurementHistory = [];
  }

  private computeTimingStability(): TimingStability {
    const { median: med, scaledMAD } = Stats.computeMAD(this.intervals);
    const avg = Stats.mean(this.intervals);
    const stdDev = Stats.standardDeviation(this.intervals, avg);
    const threshold = CONFIG.analysis.outlierThreshold * scaledMAD;

    const deviations = this.intervals.map(i => i - med);
    const typicalDeviations = deviations.filter(d => Math.abs(d) <= threshold);
    const outliers = this.intervals.filter(i => Math.abs(i - med) > threshold);

    return {
      stability: scaledMAD,
      median: med,
      mean: avg,
      stdDev,
      jitterRange: {
        min: Math.min(...deviations),
        max: Math.max(...deviations),
      },
      typicalRange: {
        min: typicalDeviations.length ? Math.min(...typicalDeviations) : 0,
        max: typicalDeviations.length ? Math.max(...typicalDeviations) : 0,
      },
      withinNormal: (typicalDeviations.length / this.intervals.length) * 100,
      spikeRate: (outliers.length / this.intervals.length) * 100,
      coefficientOfVariation: Stats.coefficientOfVariation(this.intervals),
    };
  }

  private computeTimingPattern(): TimingPattern {
    if (!this.deviceSpeed) {
      throw new Error('Device speed not determined');
    }

    if (this.deviceSpeed.speedClass === 'FullSpeed' ||
        this.deviceSpeed.speedClass === 'LowSpeed') {
      return this.computeFramePattern();
    } else {
      return this.computeMicroframePattern();
    }
  }

  private computeFramePattern(): FramePattern {
    const expectedInterval = 1000000 / this.effectiveRate;
    const framesPerPoll = Math.round(expectedInterval / USB_CONSTANTS.FRAME_DURATION_US);

    // 计算帧内位置的一致性
    const framePositions = this.timestamps.map(ts =>
      (ts * 1000) % (framesPerPoll * USB_CONSTANTS.FRAME_DURATION_US)
    );
    const { scaledMAD } = Stats.computeMAD(framePositions);
    const consistency = Math.max(0, 100 - (scaledMAD / expectedInterval * 100));

    let explanation: string;
    switch (framesPerPoll) {
      case 1:
        explanation = '每帧轮询一次 (1ms) - 全速最大性能';
        break;
      case 2:
        explanation = '每 2 帧轮询一次 (2ms) - 500Hz 全速';
        break;
      case 4:
        explanation = '每 4 帧轮询一次 (4ms) - 250Hz 全速';
        break;
      case 8:
        explanation = '每 8 帧轮询一次 (8ms) - 125Hz 标准';
        break;
      default:
        explanation = `每 ${framesPerPoll} 帧轮询一次`;
    }

    const pattern = Array.from({ length: 8 }, (_, i) => ({
      active: i % framesPerPoll === 0,
      label: `F${i + 1}`,
    }));

    return {
      type: 'frame',
      score: consistency,
      explanation,
      framesPerPoll,
      pattern,
    };
  }

  private computeMicroframePattern(): MicroframePattern {
    const phases = new Array(8).fill(0);
    const total = this.timestamps.length;

    for (const ts of this.timestamps) {
      const us = ts * 1000;
      const phase = Math.floor(
        (us % USB_CONSTANTS.FRAME_DURATION_US) / USB_CONSTANTS.MICROFRAME_DURATION_US
      );
      if (phase >= 0 && phase < 8) {
        phases[phase]++;
      }
    }

    const expectedPhasesUsed = Math.min(
      8,
      Math.round(this.effectiveRate / USB_CONSTANTS.FULL_SPEED_MAX_RATE)
    );

    // 找到主要相位
    const dominantPhase = phases.indexOf(Math.max(...phases));

    // 计算相位分布的熵
    let entropy = 0;
    for (const count of phases) {
      if (count > 0) {
        const p = count / total;
        entropy -= p * Math.log2(p);
      }
    }

    const maxEntropy = Math.log2(8);
    const phaseSpread = entropy / maxEntropy; // 0-1，越低越集中

    // 计算得分
    let score: number;
    let explanation: string;

    if (expectedPhasesUsed >= 8) {
      const activePhasesCount = phases.filter(
        p => p > total * CONFIG.analysis.phaseActiveThreshold
      ).length;
      score = (activePhasesCount / 8) * 100;
      explanation = `使用 ${activePhasesCount}/8 个微帧 (${this.effectiveRate.toFixed(0)}Hz 高速设备预期)`;
    } else {
      score = 100 * (1 - phaseSpread);
      explanation = `${this.effectiveRate.toFixed(0)}Hz 高速轮询的微帧一致性`;
    }

    return {
      type: 'microframe',
      phases,
      score,
      explanation,
      expectedPhasesUsed,
      dominantPhase,
      phaseSpread,
    };
  }

  private computeReliabilityLadder(): ReliabilityLadder {
    const percentiles = Stats.computePercentiles(this.intervals);
    const factor = percentiles.p9999 / percentiles.p50;

    // 计算一致性得分
    const spread = percentiles.p99 / percentiles.p50;
    const consistency = Math.max(0, 100 - (spread - 1) * 50);

    return {
      percentiles,
      factor,
      nominalInterval: percentiles.p50,
      consistency,
    };
  }

  private detectBurstPatterns(): BurstAnalysis {
    const { scaledMAD } = Stats.computeMAD(this.intervals);
    const bursts: BurstInfo[] = [];
    const windowSize = Math.min(
      CONFIG.analysis.burstWindowSize,
      Math.floor(this.intervals.length / 10)
    );

    if (windowSize < 10) {
      return {
        bursts: [],
        periodic: false,
        periodicity: 0,
        totalAffected: 0,
        affectedPercentage: 0,
      };
    }

    // 滑动窗口检测高抖动区域
    for (let i = 0; i <= this.intervals.length - windowSize; i += Math.floor(windowSize / 2)) {
      const window = this.intervals.slice(i, i + windowSize);
      const windowMAD = Stats.computeMAD(window);

      if (windowMAD.scaledMAD > scaledMAD * 2) {
        bursts.push({
          start: i,
          end: Math.min(i + windowSize, this.intervals.length),
          severity: windowMAD.scaledMAD / scaledMAD,
          avgInterval: Stats.mean(window),
        });
      }
    }

    // 合并重叠区域
    const merged = this.mergeBursts(bursts);

    // 检测周期性
    let periodic = false;
    let periodicity = 0;

    if (merged.length > 2) {
      const burstIntervals = [];
      for (let i = 1; i < merged.length; i++) {
        burstIntervals.push(merged[i].start - merged[i - 1].start);
      }

      const avgInterval = Stats.mean(burstIntervals);
      const cv = Stats.coefficientOfVariation(burstIntervals);

      periodic = cv < 20; // CV < 20% 认为是周期性
      periodicity = periodic ? avgInterval : 0;
    }

    const totalAffected = merged.reduce((sum, b) => sum + (b.end - b.start), 0);

    return {
      bursts: merged,
      periodic,
      periodicity,
      totalAffected,
      affectedPercentage: (totalAffected / this.intervals.length) * 100,
    };
  }

  private mergeBursts(bursts: BurstInfo[]): BurstInfo[] {
    if (bursts.length === 0) return [];

    const merged: BurstInfo[] = [];
    let current = { ...bursts[0] };

    for (let i = 1; i < bursts.length; i++) {
      if (bursts[i].start <= current.end) {
        current.end = Math.max(current.end, bursts[i].end);
        current.severity = Math.max(current.severity, bursts[i].severity);
      } else {
        merged.push(current);
        current = { ...bursts[i] };
      }
    }

    merged.push(current);
    return merged;
  }

  private computeMeasurementConfidence(): ConfidenceResult {
    if (!this.deviceSpeed) {
      return {
        score: 0,
        grade: 'critical',
        issues: [],
        recommendations: [],
      };
    }

    // 从高分开始，根据实际问题扣分
    let score = 100;
    const issues: ConfidenceIssue[] = [];
    const recommendations: string[] = [];

    const isHighSpeed = this.deviceSpeed.speedClass === 'HighSpeed';
    const is8kHz = this.effectiveRate > 4000;

    // 对于高速设备，浏览器精度是已知限制，给一个提示但不过度扣分
    if (isHighSpeed) {
      if (is8kHz) {
        score -= 15; // 轻微扣分
        issues.push({
          type: 'info',
          icon: 'ℹ️',
          message: `8kHz 高速设备，浏览器精度约100μs，轮询率测量仍然可靠`,
        });
      } else {
        issues.push({
          type: 'success',
          icon: '✓',
          message: '高速设备测量正常',
        });
      }
    } else {
      // 全速/低速设备，精度充足
      issues.push({
        type: 'success',
        icon: '✓',
        message: '设备速度适合浏览器精确测量',
      });
    }

    // 检测事件批处理（这个确实影响准确性）
    const zeros = this.intervals.filter(i => i < 10).length;
    const zeroRatio = zeros / this.intervals.length;

    if (zeroRatio > 0.01) {
      // 根据比例适度扣分，但不要太狠
      const penalty = Math.min(25, zeroRatio * 100);
      score -= penalty;

      if (zeroRatio > 0.1) {
        issues.push({
          type: 'warning',
          icon: '⚠️',
          message: `${(zeroRatio * 100).toFixed(1)}% 的数据被批量处理，可能是后台程序干扰`,
        });
        recommendations.push('关闭后台程序可以提高测量精度');
      } else {
        issues.push({
          type: 'info',
          icon: 'ℹ️',
          message: `少量数据 (${(zeroRatio * 100).toFixed(1)}%) 被批量处理，影响不大`,
        });
      }
    }

    // 样本数评估
    if (this.intervals.length > 10000) {
      issues.push({
        type: 'success',
        icon: '✓',
        message: `采集了 ${this.intervals.length.toLocaleString()} 个样本，数据充足`,
      });
    } else if (this.intervals.length > 1000) {
      issues.push({
        type: 'success',
        icon: '✓',
        message: `样本数量足够 (${this.intervals.length.toLocaleString()})`,
      });
    } else if (this.intervals.length < 500) {
      score -= 15;
      issues.push({
        type: 'warning',
        icon: '⚠️',
        message: `样本较少 (${this.intervals.length})，可以增加测试时间`,
      });
    }

    // 检测数据一致性（用CV来判断）
    const cv = Stats.coefficientOfVariation(this.intervals);
    if (cv < 50) {
      issues.push({
        type: 'success',
        icon: '✓',
        message: '数据波动在正常范围内',
      });
    } else if (cv > 100) {
      score -= 10;
      issues.push({
        type: 'warning',
        icon: '⚠️',
        message: '数据波动较大，可能受系统负载影响',
      });
    }

    // 确定等级
    score = Math.max(0, Math.min(100, score));
    let grade: ConfidenceResult['grade'];

    if (score >= GRADE_THRESHOLDS.confidence.excellent) {
      grade = 'excellent';
    } else if (score >= GRADE_THRESHOLDS.confidence.good) {
      grade = 'good';
    } else if (score >= GRADE_THRESHOLDS.confidence.fair) {
      grade = 'fair';
    } else if (score >= GRADE_THRESHOLDS.confidence.poor) {
      grade = 'poor';
    } else {
      grade = 'critical';
    }

    // 添加总结性信息
    if (issues.length === 0) {
      issues.push({
        type: 'success',
        icon: '✓',
        message: '所有测量指标都在可靠范围内',
      });
    }

    return { score, grade, issues, recommendations };
  }

  // ==================== 数据导出 ====================

  exportData(result: AnalysisResult): void {
    const exportData = {
      ...result,
      exportTime: new Date().toISOString(),
      version: '1.0.0',
    };

    const blob = new Blob(
      [JSON.stringify(exportData, null, 2)],
      { type: 'application/json' }
    );

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usb-hid-analysis-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
